import { useMemo } from 'react';
import type { Action, Module } from '@mygmao/shared';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook RBAC frontend (US-DASH-03 + US-ADM-03).
 *
 *  - `can(module, action)` : true si l'user a la permission `module.action`,
 *    ou si super-admin (bypass via `*`).
 *  - `hasModuleAccess(module)` : true si l'user a au moins une action sur ce
 *    module — pratique pour filtrer la sidebar.
 *  - `isPlatformAdmin` : raccourci pour le menu spécifique super-admin.
 *
 * Les permissions viennent de `AuthContext.user.permissions` (chargées
 * depuis `/auth/me` + `/auth/login`).
 */
export interface PermissionsApi {
  can(module: Module, action: Action): boolean;
  hasModuleAccess(module: Module): boolean;
  isPlatformAdmin: boolean;
}

export function usePermissions(): PermissionsApi {
  const { user } = useAuth();

  return useMemo<PermissionsApi>(() => {
    const codes = user?.permissions ?? [];
    const isSuperAdmin = codes.includes('*') || Boolean(user?.is_platform_admin);

    function can(module: Module, action: Action): boolean {
      if (isSuperAdmin) return true;
      if (codes.includes(`${module}.${action}`)) return true;
      if (codes.includes(`${module}.manage`)) return true; // wildcard module
      return false;
    }

    function hasModuleAccess(module: Module): boolean {
      if (isSuperAdmin) return true;
      return codes.some((c) => c.startsWith(`${module}.`));
    }

    return {
      can,
      hasModuleAccess,
      isPlatformAdmin: isSuperAdmin,
    };
  }, [user]);
}
